import { faChevronsDown } from "@fortawesome/pro-regular-svg-icons/faChevronsDown";
import { faChevronsUp } from "@fortawesome/pro-regular-svg-icons/faChevronsUp";
import { faDatabase } from "@fortawesome/pro-regular-svg-icons/faDatabase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Link as MUILink, Stack, Typography, useTheme } from "@mui/material";
import { MprcSpinnerIndicator } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { FeedContentLoadStatus } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useCardContext } from "../../CardBuilder";
import { DefaultAttach } from "../common/DefaultAttach";
import { DefaultMedia } from "../common/DefaultMedia";
import { DefaultOptions } from "../common/DefaultOptions";
import { CommentButton, WhoLikesButton, WhoViewButton } from "../common/Interaction";
import { StickerBox } from "../common/StickerBox";
import { RightImage } from "../common/Thumbnail";

///
const ViewMoreButton = () => {
    const {
        pub: { publicationId },
        localContent,
        onUpdateLocalContent,
    } = useCardContext();


    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    ///
    const loadContent = () => {

        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                onUpdateLocalContent({
                    content: result.data,
                    contentLoadStatus: FeedContentLoadStatus.AllContentLoaded,
                    state: "expanded"
                });

            })
            .catch((e) => MprcRequestSnackbarErrorReport(e));
    };

    const toogleState = () => {
        if (localContent.contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand) loadContent();
        else onUpdateLocalContent({ ...localContent, state: localContent.state == "expanded" ? "contracted" : "expanded" });
    };

    return (
        <MUILink
            color="inherit"
            onClick={toogleState}
        >
            <Typography variant="body1" color="primary.light" component="div">
                <Stack spacing={0.5} direction="row" alignItems="center">
                    {<FontAwesomeIcon icon={localContent.state == "expanded" ? faChevronsUp : faChevronsDown} size="xs" />}
                    <span>{localContent.state == "expanded" ? t("Feed.Card.Common.CollapseText") : t("Feed.Card.Common.ExpandText")}</span>
                </Stack>
            </Typography>
        </MUILink>
    );
};

///
export const PublicationAdminTitle = () => {
    const { pub, pub: { expired, availableInTheFuture } } = useCardContext();
    const { t } = useTranslation("knowledge");
    const theme = useTheme();

    const isBlocked = pub.blocked?.blockedOn != undefined;

    const color = isBlocked
        ? theme.palette.warning.main
        : expired || availableInTheFuture
            ? theme.palette.muted.light
            : theme.palette.common.black;

    return (
        <div>
            <Link to={KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.masterPublication?.publicationId)}>
                <Box sx={{ color: color }}>
                    <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                        <span title={pub.title}>{pub.title}</span>
                    </MprcFeedTypography>
                </Box>
            </Link>

            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
                {expired && <StickerBox name={t("Feed.Card.Common.ExpiredText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {availableInTheFuture && <StickerBox name={t("Feed.Card.Common.DisplayOnText", { date: Formaters.dayMonth(pub.viewPeriodStart) })} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {pub.sticker && <StickerBox name={pub.sticker.name} textColor={pub.sticker.textColor} backgroundColor={pub.sticker.backgroundColor} />}
            </Stack>
        </div>
    );
};

///
export const PublicationAdminContent = () => {
    const {
        pub: { content },
        localContent
    } = useCardContext();

    const fullContent = localContent.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary
        ? content ?? ""
        : localContent.content ?? "";

    if (localContent.state == "expanded" && localContent.contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand)
        return <MprcSpinnerIndicator />;

    return (
        <MprcFeedTypography variant="content" component="div" className="ql-editor">
            <div dangerouslySetInnerHTML={{ __html: localContent.state == "expanded" ? fullContent : "" }}></div>
        </MprcFeedTypography>
    );

};

export const PublicationAdminAttach = () => {
    const { localContent } = useCardContext();
    return localContent.state == "expanded" ? <DefaultAttach /> : <></>;
};

export const PublicationAdminMedia = () => {
    const { localContent } = useCardContext();
    if (localContent.state != "expanded") return null;
    return <DefaultMedia />;
};

export const PublicationAdminRightThumbnail = () => {
    const { localContent } = useCardContext();
    if (localContent.state != "expanded") return null;
    return <RightImage />;
};

export const PublicationAdminFooter = () => {

    const { pub: { otherInfo: { totalStorageSize } } } = useCardContext();
    const { t } = useTranslation("knowledge");

    const StorageSizeBox =
        <Typography variant={"iconText"} component="div">
            <Stack spacing={1} direction="row" alignItems="center">
                <FontAwesomeIcon icon={faDatabase} title={t("Feed.PublicationAdmin.StorageSizeTooltip")} />
                <span> {Formaters.fileSize(totalStorageSize)}</span>
            </Stack>
        </Typography>;

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <ViewMoreButton />
            </Grid>
            <Grid>
                <Grid container spacing={3} alignItems="center" justifyContent="center">
                    <Grid>
                        <WhoViewButton />
                    </Grid>
                    <Grid>
                        <WhoLikesButton />
                    </Grid>
                    <Grid>
                        <CommentButton />
                    </Grid>
                    <Grid>
                        {totalStorageSize > 0 && StorageSizeBox}
                    </Grid>
                </Grid>
            </Grid>
            <Grid>
                <DefaultOptions />
            </Grid>
        </Grid>
    );
};
